import React, { useState, useEffect, useCallback } from 'react';
import { getCoupons, createCoupon, updateCoupon, deleteCoupon } from '../hooks/useStoreApi';
import { Plus, Ticket, Edit2, Trash2, X } from 'lucide-react';
import { AdminTabEmpty } from './store/admin/AdminTabEmpty';

// Coupons Tab Component (extracted from StoreAdmin.jsx)
export const CouponsTab = ({ storeId, onRegisterRefresh, chatId, intent, onIntentConsumed }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // External intent — AI chat or command palette can ask us to open the
    // create modal directly. Consume once, then clear so it doesn't re-fire.
    useEffect(() => {
        if (intent === 'create') {
            setEditingCoupon(null);
            setShowModal(true);
            onIntentConsumed?.();
        }
    }, [intent, onIntentConsumed]);

    // Fetch coupons
    const fetchCoupons = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getCoupons(storeId, chatId);
            setCoupons(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [storeId]);

    // Register refresh handler
    useEffect(() => {
        if (onRegisterRefresh) {
            onRegisterRefresh('coupons', async () => {
                await fetchCoupons();
            });
        }
    }, [onRegisterRefresh, fetchCoupons]);

    // Initial fetch
    useEffect(() => {
        fetchCoupons();
    }, [fetchCoupons]);

    const handleDelete = async (couponCode) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק את הקופון?')) return;

        try {
            await deleteCoupon(storeId, couponCode, chatId);
            fetchCoupons();
        } catch (err) {
            alert('שגיאה במחיקת הקופון: ' + err.message);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warning-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-danger-50 border border-danger-100 rounded-lg p-4 text-danger-700">
                שגיאה בטעינת קופונים: {error}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-line">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-ink-strong">ניהול קופונים</h2>
                    <button
                        onClick={() => { setEditingCoupon(null); setShowModal(true); }}
                        className="flex items-center gap-2 px-4 py-2 bg-warning-500 text-white rounded-lg hover:bg-warning-600 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        <span>הוסף קופון</span>
                    </button>
                </div>

                {!coupons || coupons.length === 0 ? (
                    <AdminTabEmpty
                        icon={Ticket}
                        tone="secondary"
                        title="קופונים — דרך פשוטה להחזיר לקוחות"
                        body="קוד הנחה הוא תמריץ קטן שיכול לעשות הרבה: לקוח שכבר התלבט יסיים את הקנייה, ולקוח שכבר קנה ירצה לחזור. אפשר ליצור קופון לאחוז הנחה, סכום קבוע, או הזמנה מינימלית — הכל בלי קוד."
                        examples={[
                            'WELCOME10 — 10% הנחה לרכישה ראשונה כדי למשוך לקוחות חדשים',
                            'SHABBAT — ₪20 הנחה מעל ₪100, פעיל ביום חמישי',
                            'VIP — קופון אישי שאפשר לשלוח לקוחות מועדפים בוואטסאפ',
                        ]}
                        primaryAction={{
                            label: 'ליצור קופון ראשון',
                            onClick: () => { setEditingCoupon(null); setShowModal(true); },
                        }}
                    />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {coupons.map(coupon => (
                            <div key={coupon.code} className="flex items-center justify-between p-4 border border-line rounded-lg hover:bg-surface-muted">
                                <div>
                                    <h3 className="font-semibold text-ink-strong font-mono">{coupon.code}</h3>
                                    <p className="text-sm text-ink-muted">
                                        {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₪${coupon.discount_value}`} הנחה
                                    </p>
                                    {coupon.discount_type !== 'percentage' && coupon.remaining_balance !== undefined && (
                                        <p className="text-xs text-ink-subtle">
                                            יתרה נותרת: ₪{parseFloat(coupon.remaining_balance).toFixed(2)}
                                        </p>
                                    )}
                                    {coupon.max_uses && (
                                        <p className="text-xs text-ink-subtle">שימושים: {coupon.used_count || 0} / {coupon.max_uses}</p>
                                    )}
                                    {coupon.active === false && (
                                        <span className="text-xs text-danger-600">לא פעיל</span>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => { setEditingCoupon(coupon); setShowModal(true); }}
                                        className="p-2 text-info-600 hover:bg-info-50 rounded-lg"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(coupon.code)}
                                        className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showModal && (
                <CouponModal
                    storeId={storeId}
                    coupon={editingCoupon}
                    onClose={() => { setShowModal(false); setEditingCoupon(null); }}
                    onSave={() => { setShowModal(false); setEditingCoupon(null); fetchCoupons(); }}
                    chatId={chatId}
                />
            )}
        </div>
    );
};

// Coupon Modal Component (extracted)
const CouponModal = ({ storeId, coupon, onClose, onSave, chatId }) => {
    const [formData, setFormData] = useState({
        code: coupon?.code || '',
        discount_type: coupon?.discount_type || 'percentage',
        discount_value: coupon?.discount_value || '',
        max_uses: coupon?.max_uses || '',
        min_order_value: coupon?.min_order_value || '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const couponData = {
                code: formData.code.toUpperCase(),
                discount_type: formData.discount_type,
                discount_value: parseFloat(formData.discount_value),
            };

            if (formData.max_uses) {
                couponData.max_uses = parseInt(formData.max_uses);
            }
            if (formData.min_order_value) {
                couponData.min_order_value = parseFloat(formData.min_order_value);
            }

            if (coupon) {
                await updateCoupon(storeId, coupon.code, couponData, chatId);
            } else {
                await createCoupon(storeId, couponData, chatId);
            }

            onSave();
        } catch (err) {
            setError(err.message || 'שגיאה בשמירת הקופון');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-ink-strong">{coupon ? 'ערוך קופון' : 'הוסף קופון חדש'}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-surface-muted rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {error && (
                    <div className="mb-4 p-3 bg-danger-50 border border-danger-100 rounded-lg text-danger-700 text-sm">
                        {error}
                    </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">קוד קופון</label>
                        <input
                            type="text"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong font-mono"
                            placeholder="SUMMER2024"
                            required
                            disabled={!!coupon}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">סוג הנחה</label>
                        <select
                            value={formData.discount_type}
                            onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                        >
                            <option value="percentage">אחוזים (%)</option>
                            <option value="fixed">סכום קבוע (₪)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">
                            ערך ההנחה {formData.discount_type === 'percentage' ? '(%)' : '(₪)'}
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.discount_value}
                            onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">מספר שימושים מקסימלי (אופציונלי)</label>
                        <input
                            type="number"
                            value={formData.max_uses}
                            onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            placeholder="ללא הגבלה"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">סכום הזמנה מינימלי (₪) (אופציונלי)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={formData.min_order_value}
                            onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            placeholder="0"
                        />
                    </div>
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-line-strong rounded-lg hover:bg-surface-muted transition-colors text-ink-strong"
                            disabled={loading}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-warning-500 text-white rounded-lg hover:bg-warning-600 transition-colors text-white disabled:opacity-50"
                            disabled={loading}
                        >
                            {loading ? 'שומר...' : (coupon ? 'שמור' : 'הוסף')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CouponsTab;
